import React, { useState } from 'react';
import { customerService, interactionService } from '../services/api';
import { useAuthStore } from '../store';
import { GlassPanel, GlassButton, GlassInput, GlassCard } from '../components/GlassUI';
import { FaWhatsapp, FaPrint } from 'react-icons/fa';
import QrCode from '../components/QrCode';

export const FeedbackFormPage: React.FC = () => {
  const { user } = useAuthStore();

  const [step, setStep] = useState(1);
  const [customer, setCustomer] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState<any>(null);

  const [formData, setFormData] = useState({
    cellNumber: '',
    customerName: '',
    whatsappNumber: '',
    dateOfBirth: '',
    anniversary: '',
    location: '',
    jewelleryType: '',
    categories: [] as string[],
    profession: [] as string[],
    professionOther: '',
    customerStatus: 'NEW',
    savingFuturePlanCard: '',
    purchaseStatus: 'NOT_PURCHASED',
    nonPurchaseReason: '',
    marketingChannel: [] as string[],
    marketingOther: '',
    reference1Name: '',
    reference1Phone: '',
    reference2Name: '',
    reference2Phone: '',
    notes: '',
  });

  const update = (field: string, value: any) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  // vCard encoded into the "Save our Store Contact" QR (rendered locally).
  const storeVCard = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${user?.name || 'Store'}`,
    `ORG:${user?.name || 'Store'}`,
    `TEL;TYPE=WORK,VOICE:${user?.phoneNumber || '+919999999999'}`,
    'END:VCARD',
  ].join('\n');

  const toggleArray = (field: string, value: string) => {
    const arr = formData[field as keyof typeof formData] as string[];
    if (arr.includes(value)) {
      update(field, arr.filter(v => v !== value));
    } else {
      update(field, [...arr, value]);
    }
  };

  // Step 1: Phone lookup
  const handlePhoneCheck = async () => {
    if (!formData.cellNumber) { alert('Please enter cell number'); return; }
    setLoading(true);
    try {
      const res = await customerService.checkCustomer(formData.cellNumber);
      if (res.data.exists) {
        setCustomer(res.data.customer);
        const c = res.data.customer;
        let prof: string[] = [];
        try { const p = JSON.parse(c.profession); if (Array.isArray(p)) prof = p; } catch { prof = c.profession ? [c.profession] : []; }
        let mkt: string[] = [];
        try { const m = JSON.parse(c.marketingChannel); if (Array.isArray(m)) mkt = m; } catch { mkt = c.marketingChannel ? [c.marketingChannel] : []; }
        setFormData(prev => ({
          ...prev,
          customerName: c.customerName || prev.customerName,
          whatsappNumber: c.whatsappNumber || prev.whatsappNumber,
          dateOfBirth: c.dateOfBirth ? c.dateOfBirth.split('T')[0] : prev.dateOfBirth,
          anniversary: c.anniversary ? c.anniversary.split('T')[0] : prev.anniversary,
          location: c.location || prev.location,
          profession: prof,
          marketingChannel: mkt,
          reference1Name: c.reference1Name || prev.reference1Name,
          reference1Phone: c.reference1Phone || prev.reference1Phone,
          reference2Name: c.reference2Name || prev.reference2Name,
          reference2Phone: c.reference2Phone || prev.reference2Phone,
          customerStatus: c.isRepeatedCustomer ? 'OLD' : 'NEW',
        }));
      }
      setStep(2);
    } catch (error) {
      alert('Error checking customer');
    } finally { setLoading(false); }
  };

  // Step 5: Final submit
  const handleSubmit = async () => {
    if (!formData.customerName || !formData.cellNumber) {
      alert('Customer name and cell number are required');
      return;
    }
    setLoading(true);
    try {
      const custRes = await customerService.createOrUpdate({
        customerName: formData.customerName,
        cellNumber: formData.cellNumber,
        whatsappNumber: formData.whatsappNumber,
        dateOfBirth: formData.dateOfBirth || undefined,
        anniversary: formData.anniversary || undefined,
        location: formData.location,
        profession: formData.profession,
        professionOther: formData.professionOther,
        marketingChannel: formData.marketingChannel,
        marketingOther: formData.marketingOther,
        reference1Name: formData.reference1Name,
        reference1Phone: formData.reference1Phone,
        reference2Name: formData.reference2Name,
        reference2Phone: formData.reference2Phone,
        customerStatus: formData.customerStatus,
        sourceType: formData.customerStatus === 'OLD' ? 'REPEAT' : 'NEW',
      });

      const customerId = custRes.data.customer?.id || customer?.id;
      if (customerId) {
        await interactionService.create({
          customerId,
          interactionType: 'WALK_IN',
          jewelleryTypeInquired: formData.jewelleryType,
          categories: formData.categories,
          purchaseStatus: formData.purchaseStatus,
          nonPurchaseReason: formData.nonPurchaseReason,
          savingFuturePlanCard: formData.savingFuturePlanCard,
          notes: formData.notes,
        });
      }

      setSubmitted({ ...custRes.data.customer, visitNumber: 'VST-' + String(Math.random()).slice(2, 8) });
      setStep(6);
    } catch (error) {
      alert('Error saving form');
    } finally { setLoading(false); }
  };

  const Checkbox = ({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) => (
    <label className='flex items-center gap-2 cursor-pointer'>
      <input type='checkbox' checked={checked} onChange={onChange} className='w-4 h-4 accent-slate-900' />
      <span className='text-slate-700 text-sm'>{label}</span>
    </label>
  );

  const catOptions = ['Gold', 'Diamond', 'Silver', 'Other Stone'];
  const profOptions = ['Doctor', 'Lawyer', 'Teacher', 'Self Business', 'Other'];
  const mktOptions = ['Facebook', 'Instagram', 'Social Media', 'Tele Calling', 'If any'];

  return (
    <div className='min-h-screen bg-slate-50 p-4 md:p-8 text-slate-900'>
      <div className='max-w-3xl mx-auto'>
        {/* Step 1: Phone */}
        {step === 1 && (
          <GlassPanel className='p-8'>
            <h2 className='text-3xl font-bold text-slate-900 mb-2'>Customer Form</h2>
            <p className='text-slate-500 mb-6'>Enter phone number to look up existing customer, or leave blank for new entry.</p>
            <div>
              <label className='block text-slate-700 font-semibold mb-2'>Cell Number</label>
              <div className='flex gap-2'>
                <GlassInput type='tel' placeholder='+91...' value={formData.cellNumber}
                  onChange={e => update('cellNumber', e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handlePhoneCheck()} />
                <GlassButton onClick={handlePhoneCheck} disabled={loading} variant='primary'>
                  {loading ? '...' : 'Search'}
                </GlassButton>
              </div>
            </div>
            <div className='mt-4 text-center'>
              <GlassButton variant='secondary' onClick={() => { setCustomer(null); setStep(2); }}>
                Skip - New Customer
              </GlassButton>
            </div>
          </GlassPanel>
        )}

        {/* Step 2-5: Full Form */}
        {step >= 2 && step <= 5 && !submitted && (
          <div className='space-y-6'>
            {/* Header */}
            <GlassPanel className='p-6'>
              <div className='flex items-center justify-between flex-wrap gap-4'>
                <div>
                  <h2 className='text-2xl font-bold text-slate-900'>Customer Visit Form</h2>
                  <p className='text-slate-500 text-sm mt-1'>Visit #: <span className='font-mono font-semibold text-slate-700'>VST-{String(Math.random()).slice(2, 8)}</span></p>
                </div>
                <div className='text-right text-sm text-slate-600'>
                  <p><strong>Date:</strong> {new Date().toLocaleDateString('en-IN')}</p>
                  <p><strong>Staff:</strong> {user?.name || user?.staffName || 'N/A'}</p>
                </div>
              </div>
            </GlassPanel>

            {/* Step 2: Personal Details */}
            {step === 2 && (
              <GlassPanel className='p-6'>
                <h3 className='text-xl font-bold text-slate-900 mb-4'>Personal Details</h3>
                {customer && (
                  <GlassCard className='mb-4 bg-blue-50 border-blue-200'>
                    <p className='text-blue-800 font-medium'>Returning Customer: {customer.customerName}</p>
                  </GlassCard>
                )}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-slate-700 font-semibold mb-1 text-sm'>Customer Name *</label>
                    <GlassInput value={formData.customerName} onChange={e => update('customerName', e.target.value)} placeholder='Full name' />
                  </div>
                  <div>
                    <label className='block text-slate-700 font-semibold mb-1 text-sm'>Cell No. *</label>
                    <GlassInput value={formData.cellNumber} onChange={e => update('cellNumber', e.target.value)} placeholder='Phone number' />
                  </div>
                  <div>
                    <label className='block text-slate-700 font-semibold mb-1 text-sm'>WhatsApp No.</label>
                    <GlassInput value={formData.whatsappNumber} onChange={e => update('whatsappNumber', e.target.value)} placeholder='WhatsApp number' />
                  </div>
                  <div>
                    <label className='block text-slate-700 font-semibold mb-1 text-sm'>DOB</label>
                    <GlassInput type='date' value={formData.dateOfBirth} onChange={e => update('dateOfBirth', e.target.value)} />
                  </div>
                  <div>
                    <label className='block text-slate-700 font-semibold mb-1 text-sm'>Anniversary</label>
                    <GlassInput type='date' value={formData.anniversary} onChange={e => update('anniversary', e.target.value)} />
                  </div>
                  <div>
                    <label className='block text-slate-700 font-semibold mb-1 text-sm'>Location</label>
                    <GlassInput value={formData.location} onChange={e => update('location', e.target.value)} placeholder='City / Area' />
                  </div>
                </div>
                <div className='flex justify-between mt-6'>
                  <GlassButton variant='secondary' onClick={() => setStep(1)}>Back</GlassButton>
                  <GlassButton variant='primary' onClick={() => setStep(3)}>Next - Interests</GlassButton>
                </div>
              </GlassPanel>
            )}

            {/* Step 3: Demographics & Interests */}
            {step === 3 && (
              <GlassPanel className='p-6'>
                <h3 className='text-xl font-bold text-slate-900 mb-4'>Customer Demographics & Interests</h3>

                <div className='mb-4'>
                  <label className='block text-slate-700 font-semibold mb-1 text-sm'>Jewellery Type</label>
                  <GlassInput value={formData.jewelleryType} onChange={e => update('jewelleryType', e.target.value)} placeholder='e.g., Necklace, Ring, Bracelet...' />
                </div>

                <div className='mb-4'>
                  <label className='block text-slate-700 font-semibold mb-2 text-sm'>Category</label>
                  <div className='flex flex-wrap gap-4'>
                    {catOptions.map(c => <Checkbox key={c} label={c} checked={formData.categories.includes(c)} onChange={() => toggleArray('categories', c)} />)}
                  </div>
                </div>

                <div>
                  <label className='block text-slate-700 font-semibold mb-2 text-sm'>Profession</label>
                  <div className='flex flex-wrap gap-4 mb-2'>
                    {profOptions.map(p => <Checkbox key={p} label={p} checked={formData.profession.includes(p)} onChange={() => toggleArray('profession', p)} />)}
                  </div>
                  {formData.profession.includes('Other') && (
                    <GlassInput value={formData.professionOther} onChange={e => update('professionOther', e.target.value)} placeholder='Specify profession...' className='mt-2' />
                  )}
                </div>

                <div className='flex justify-between mt-6'>
                  <GlassButton variant='secondary' onClick={() => setStep(2)}>Back</GlassButton>
                  <GlassButton variant='primary' onClick={() => setStep(4)}>Next - Visit Context</GlassButton>
                </div>
              </GlassPanel>
            )}

            {/* Step 4: Visit & Purchase Context */}
            {step === 4 && (
              <GlassPanel className='p-6'>
                <h3 className='text-xl font-bold text-slate-900 mb-4'>Visit & Purchase Context</h3>

                <div className='mb-4'>
                  <label className='block text-slate-700 font-semibold mb-2 text-sm'>Customer Status</label>
                  <div className='flex gap-4'>
                    <Checkbox label='New Customer' checked={formData.customerStatus === 'NEW'} onChange={() => update('customerStatus', 'NEW')} />
                    <Checkbox label='Old Customer' checked={formData.customerStatus === 'OLD'} onChange={() => update('customerStatus', 'OLD')} />
                  </div>
                </div>

                <div className='mb-4'>
                  <label className='block text-slate-700 font-semibold mb-1 text-sm'>Saving Future Plan Card</label>
                  <GlassInput value={formData.savingFuturePlanCard} onChange={e => update('savingFuturePlanCard', e.target.value)} placeholder='Plan details...' />
                </div>

                <div className='mb-4'>
                  <label className='block text-slate-700 font-semibold mb-2 text-sm'>Purchase Status</label>
                  <div className='flex gap-4'>
                    <Checkbox label='Purchase' checked={formData.purchaseStatus === 'PURCHASED'} onChange={() => update('purchaseStatus', formData.purchaseStatus === 'PURCHASED' ? 'NOT_PURCHASED' : 'PURCHASED')} />
                    <Checkbox label='Non-Purchase' checked={formData.purchaseStatus === 'NOT_PURCHASED'} onChange={() => update('purchaseStatus', formData.purchaseStatus === 'NOT_PURCHASED' ? 'PURCHASED' : 'NOT_PURCHASED')} />
                  </div>
                </div>

                {formData.purchaseStatus === 'NOT_PURCHASED' && (
                  <div className='mb-4'>
                    <label className='block text-slate-700 font-semibold mb-1 text-sm'>Non-Purchase Reason</label>
                    <GlassInput value={formData.nonPurchaseReason} onChange={e => update('nonPurchaseReason', e.target.value)} placeholder='Reason...' />
                  </div>
                )}

                <div className='flex justify-between mt-6'>
                  <GlassButton variant='secondary' onClick={() => setStep(3)}>Back</GlassButton>
                  <GlassButton variant='primary' onClick={() => setStep(5)}>Next - Marketing</GlassButton>
                </div>
              </GlassPanel>
            )}

            {/* Step 5: Marketing & Referrals */}
            {step === 5 && (
              <GlassPanel className='p-6'>
                <h3 className='text-xl font-bold text-slate-900 mb-4'>Marketing & Referrals</h3>

                <div className='mb-4'>
                  <label className='block text-slate-700 font-semibold mb-2 text-sm'>How did you hear about us?</label>
                  <div className='flex flex-wrap gap-4 mb-2'>
                    {mktOptions.map(m => <Checkbox key={m} label={m} checked={formData.marketingChannel.includes(m)} onChange={() => toggleArray('marketingChannel', m)} />)}
                  </div>
                  {formData.marketingChannel.includes('If any') && (
                    <GlassInput value={formData.marketingOther} onChange={e => update('marketingOther', e.target.value)} placeholder='Specify...' className='mt-2' />
                  )}
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
                  <div>
                    <label className='block text-slate-700 font-semibold mb-1 text-sm'>Reference Name 1</label>
                    <GlassInput value={formData.reference1Name} onChange={e => update('reference1Name', e.target.value)} placeholder='Name' />
                  </div>
                  <div>
                    <label className='block text-slate-700 font-semibold mb-1 text-sm'>Reference Phone 1</label>
                    <GlassInput value={formData.reference1Phone} onChange={e => update('reference1Phone', e.target.value)} placeholder='Phone' />
                  </div>
                  <div>
                    <label className='block text-slate-700 font-semibold mb-1 text-sm'>Reference Name 2</label>
                    <GlassInput value={formData.reference2Name} onChange={e => update('reference2Name', e.target.value)} placeholder='Name' />
                  </div>
                  <div>
                    <label className='block text-slate-700 font-semibold mb-1 text-sm'>Reference Phone 2</label>
                    <GlassInput value={formData.reference2Phone} onChange={e => update('reference2Phone', e.target.value)} placeholder='Phone' />
                  </div>
                </div>

                <div className='mb-4'>
                  <label className='block text-slate-700 font-semibold mb-1 text-sm'>Additional Notes</label>
                  <textarea value={formData.notes} onChange={e => update('notes', e.target.value)}
                    placeholder='Extra notes...'
                    className='w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-500 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 transition-all'
                    rows={3} />
                </div>

                <div className='flex justify-between mt-6'>
                  <GlassButton variant='secondary' onClick={() => setStep(4)}>Back</GlassButton>
                  <GlassButton variant='primary' onClick={handleSubmit} disabled={loading}>
                    {loading ? 'Saving...' : 'Save & Submit'}
                  </GlassButton>
                </div>
              </GlassPanel>
            )}
          </div>
        )}

        {/* Step 6: Success with QR */}
        {step === 6 && submitted && (
          <GlassPanel className='p-8'>
            <div className='text-center mb-6'>
              <div className='w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4'>
                <svg className='w-8 h-8 text-green-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                </svg>
              </div>
              <h2 className='text-3xl font-bold text-slate-900 mb-2'>Form Submitted Successfully</h2>
              <p className='text-slate-500'>Visit #: <span className='font-mono font-bold text-slate-700'>{submitted.visitNumber}</span></p>
            </div>

            <GlassCard className='mb-6 bg-green-50 border-green-200 text-left'>
              <div className='space-y-2 text-sm'>
                <div className='flex justify-between'>
                  <span className='text-slate-600'>Customer</span>
                  <span className='font-semibold text-slate-900'>{submitted.customerName || formData.customerName}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-slate-600'>Phone</span>
                  <span className='font-semibold text-slate-900'>{formData.cellNumber}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-slate-600'>Date</span>
                  <span className='font-semibold text-slate-900'>{new Date().toLocaleDateString('en-IN')}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-slate-600'>Purchase</span>
                  <span className={`font-semibold ${formData.purchaseStatus === 'PURCHASED' ? 'text-green-700' : 'text-amber-700'}`}>
                    {formData.purchaseStatus === 'PURCHASED' ? 'Purchased' : 'Not Purchased'}
                  </span>
                </div>
              </div>
            </GlassCard>

            <div className='mb-6'>
              <label className='block text-slate-500 text-xs mb-2'>Additional Remarks / Notes</label>
              <div className='border-b border-slate-200 mb-3 h-8' />
              <div className='border-b border-slate-200 mb-3 h-8' />
              <div className='border-b border-slate-200 h-8' />
            </div>

            <GlassCard className='mb-6 bg-blue-50 border-blue-200'>
              <h3 className='font-bold text-slate-900 mb-3 text-center'>Save our Store Contact</h3>
              <p className='text-sm text-slate-600 mb-4 text-center'>
                Scan this QR code to save store number and check your visit status on WhatsApp.
              </p>
              <div className='flex justify-center mb-4'>
                <QrCode value={storeVCard} size={192} fileName="store-contact-qr" />
              </div>
              <a
                href={`https://wa.me/${(user?.phoneNumber || '+919999999999').replace(/[^0-9]/g, '')}`}
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex items-center justify-center gap-2 w-full px-6 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-all'
              >
                <FaWhatsapp size={20} /> Chat on WhatsApp
              </a>
            </GlassCard>

            <div className='flex gap-3'>
              <GlassButton variant='secondary' className='flex-1' onClick={() => window.print()}>
                <FaPrint className='inline mr-1' /> Print
              </GlassButton>
              <GlassButton variant='primary' className='flex-1' onClick={() => {
                setStep(1); setCustomer(null); setSubmitted(null);
                setFormData({
                  cellNumber: '', customerName: '', whatsappNumber: '', dateOfBirth: '', anniversary: '',
                  location: '', jewelleryType: '', categories: [], profession: [], professionOther: '',
                  customerStatus: 'NEW', savingFuturePlanCard: '', purchaseStatus: 'NOT_PURCHASED',
                  nonPurchaseReason: '', marketingChannel: [], marketingOther: '',
                  reference1Name: '', reference1Phone: '', reference2Name: '', reference2Phone: '', notes: '',
                });
              }}>
                New Entry
              </GlassButton>
            </div>
          </GlassPanel>
        )}
      </div>
    </div>
  );
};

export default FeedbackFormPage;
